function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Controller, useForm } from 'react-hook-form';
import { nameValidation, requiredValidation, usPhoneNumberValidation } from 'validators';
import { Page, Button, TextField, PasswordField, Card, USPhoneNumberInput, TextButton } from 'seagull';
import ChangeEmailAddressModal from "../../components/ChangeEmailAddressModal/ChangeEmailAddressModal";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { Intent } from "../../infra/access-control";
import { handleRequest } from "../../infra/utils";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import styles from "./Profile.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testPrefix = 'profile';
const title = 'Profile';
const nullIfEmpty = s => s || null;
export default function Profile() {
  const overlayContext = useOverlayContext();
  const userInfo = useStore(state => state.userInfo);
  const {
    setUserInfo,
    logBI,
    api
  } = useStoreActions();
  const {
    control,
    formState,
    handleSubmit,
    watch,
    trigger,
    clearErrors,
    reset
  } = useForm({
    mode: 'onChange'
  });
  const userLoggedInViaLink = userInfo === null || userInfo === void 0 ? void 0 : userInfo.loggedInViaLink();
  const userHasPassword = userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasPassword();
  const firstNameValidator = nameValidation('First name', {
    required: 'Please enter your first name'
  });
  const lastNameValidator = nameValidation('Last name', {
    required: 'Please enter your last name'
  });
  const currentPasswordValidator = requiredValidation(!!watch('newPassword') && 'Please enter your current password');
  const newPasswordValidator = {
    validate(password) {
      const hasPassword = !!password;
      hasPassword ? trigger('currentPassword') : clearErrors('currentPassword');
      const isValidPassword = hasPassword ? (password === null || password === void 0 ? void 0 : password.length) >= 10 : true;
      return isValidPassword || 'Use 10 or more characters';
    }
  };
  const confirmPasswordValidator = {
    validate(password) {
      return password === watch('newPassword') || 'Passwords must match';
    }
  };
  useSetDocumentTitle(title);
  const onEditUserEmail = () => {
    overlayContext.showModal({
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(ChangeEmailAddressModal, {
          onClose: dismissModal
        });
      }
    });
  };
  const onSubmit = async form => {
    logBI(10410);
    const updatedUserInfo = await handleRequest(api.auth.editUserDetails({
      name: {
        first: form.firstName,
        last: form.lastName
      },
      password: nullIfEmpty(form.newPassword),
      currentPassword: form.newPassword ? nullIfEmpty(form.currentPassword) : null,
      phone: nullIfEmpty(form.phone),
      clearPhoneNumber: !form.phone
    }));
    if (updatedUserInfo.success) {
      setUserInfo((userInfo === null || userInfo === void 0 ? void 0 : userInfo.updateUserDetails(updatedUserInfo.data)) || null);
      reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      overlayContext.showSuccessToast('Changes saved');
    } else {
      overlayContext.showErrorToast(updatedUserInfo.error.updateUserDetailsErrorText());
    }
  };
  return /*#__PURE__*/_jsx(Page, {
    header: title,
    skin: "secondary",
    "data-testid": "".concat(testPrefix, "-page"),
    actions: /*#__PURE__*/_jsx(Button, {
      intent: Intent.UpdateProfile,
      type: "submit",
      form: testPrefix,
      loading: formState.isSubmitting,
      "data-testid": "".concat(testPrefix, "-btn-save-changes"),
      children: "Save changes"
    }),
    children: /*#__PURE__*/_jsx("form", {
      id: testPrefix,
      onSubmit: handleSubmit(onSubmit),
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.root,
        children: [/*#__PURE__*/_jsxs(Section, {
          title: "Personal details",
          children: [/*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "firstName",
            defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
            rules: firstNameValidator,
            render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "First name",
              "data-testid": "".concat(testPrefix, "-first-name")
            }))
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "lastName",
            defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName(),
            rules: lastNameValidator,
            render: renderProps => /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "Last name",
              "data-testid": "".concat(testPrefix, "-last-name")
            }))
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.emailContainer,
            children: [/*#__PURE__*/_jsx(TextField, {
              className: styles.field,
              value: userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
              label: "Email address",
              type: "email",
              "data-testid": "".concat(testPrefix, "-email"),
              disabled: true
            }), /*#__PURE__*/_jsx(TextButton, {
              onClick: onEditUserEmail,
              underline: "none",
              "data-testid": "".concat(testPrefix, "-email-edit-button"),
              type: "button",
              children: "Edit"
            })]
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "phone",
            defaultValue: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone()) || undefined,
            rules: usPhoneNumberValidation(),
            render: renderProps => /*#__PURE__*/_jsx(USPhoneNumberInput, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "Phone number",
              "data-testid": "".concat(testPrefix, "-phone")
            }))
          })]
        }), (userHasPassword || userLoggedInViaLink) && /*#__PURE__*/_jsxs(Section, {
          title: "Password",
          children: [!userLoggedInViaLink && /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "currentPassword",
            rules: currentPasswordValidator,
            render: renderProps => /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "Current password",
              "data-testid": "".concat(testPrefix, "-current-password")
            }))
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "newPassword",
            rules: newPasswordValidator,
            render: renderProps => /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "New password",
              "data-testid": "".concat(testPrefix, "-new-password")
            }))
          }), /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "confirmPassword",
            rules: confirmPasswordValidator,
            render: renderProps => /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({
              className: styles.field
            }, connectControllerToTextField(renderProps)), {}, {
              label: "Confirm password",
              "data-testid": "".concat(testPrefix, "-confirm-password")
            }))
          })]
        })]
      })
    })
  });
}
const Section = props => {
  return /*#__PURE__*/_jsx(Card, {
    padding: "m",
    autoHeight: true,
    title: title,
    children: /*#__PURE__*/_jsx("div", {
      className: styles.content,
      children: props.children
    })
  });
};